import { Link, useLocation, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useState, useEffect, useRef, useCallback } from "react";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";
import Avatar from "@mui/material/Avatar";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { Circle as CircleIcon } from "@phosphor-icons/react/dist/ssr/Circle";
import { SignOut } from "@phosphor-icons/react";
import { isNavItemActive } from "../../../helper/libs/is-nav-item-active";
import { navIcons } from "./nav-icons";
import { useSideNavWidth, useSideNavCollapsed, sideNavLayout } from "../../../helper/hooks/useSideNavLayout";
import { logout } from "../../../redux/slices/authSlice";
import SideNavScopeFilter from "./SideNavScopeFilter";
import { routeOf, loginRoute, accountRoute } from "../../../utils/appRoutes";

// ── MarketSync brand palette — the ONLY colors used in this component,
// matching the public landing page exactly (cream surface + near-black
// text + gold/orange accent). No purple/dark theme. ──────────────────
const C = {
  bg: "#f7f2ea",
  surface: "#ffffff",
  text: "#14161c",
  textSub: "#6b6459",
  textMuted: "#a19a8c",
  border: "#e6dfd0",
  borderSoft: "#efe7d8",
  orangeFrom: "#fbbf24",
  orangeTo: "#e4870e",
};

// A module with children only navigates when it's genuinely its own page.
// `is_category` is the real signal (Module schema — category/container
// modules still carry a route_path because the field is DB-required, but
// is_category:true marks it as not a real page). The dashboard-path check
// is a belt-and-suspenders fallback for any container module whose
// is_category flag isn't set correctly in existing data — its placeholder
// route_path has been observed defaulting to the dashboard route, which
// would otherwise still incorrectly redirect there on click. "Master
// Settings" is the same situation under a different placeholder path —
// it's a pure category by name/convention (the app's own original code
// special-cased it by this exact name for the same reason) even though
// neither of the above two checks catches its specific placeholder.
const isDashboardPath = (route) => [routeOf("admin.dashboard"), routeOf("customer.dashboard")].filter(Boolean).includes(route);
const CATEGORY_ONLY_NAMES = ["Master Settings"];
const isRealPage = (m) => !!m.route_path && !m.is_category && !m.is_group && !isDashboardPath(m.route_path) && !CATEGORY_ONLY_NAMES.includes(m.name);

// Modules that moved into Settings → their own tab (SettingsPage.jsx)
// instead of the sidebar — same treatment as Master Settings above, just
// for modules that AREN'T pure categories (real pages, hidden by name/route
// rather than by is_category). Trimmed name compare — the DB's own
// "Subscription" module row carries a stray leading space.
// "Store Settings" is a tab inside the MS DropShip Settings page (MSDropShipSettingsPage.jsx) — its module only
// carries the tab's permissions, so it never shows as a sidebar item.
const SIDEBAR_HIDDEN_NAMES = ["Subscription", "Store Settings"];
// Account (/admin/account, /dashboard/account) lives in the navbar profile menu
// and the sidebar profile card only — never as a sidebar item, on either side.
const sidebarHiddenRoutes = () => [routeOf("subscription"), routeOf("admin.account"), routeOf("customer.account")].filter(Boolean);
// Subscription is hidden on the admin side, but shown in the customer-side sidebar.
const isSidebarHidden = (m, isAdminArea = true) => {
  const route = String(m.route_path || "").trim();
  const isSubscription = String(m.name || "").trim() === "Subscription" || route === routeOf("subscription");
  if (isSubscription) return isAdminArea;
  return SIDEBAR_HIDDEN_NAMES.includes(String(m.name || "").trim()) || sidebarHiddenRoutes().includes(route);
};

// ── Stylish CSS injected once ─────────────────────────────────────
const SIDENAV_CSS = `
  @keyframes sn-fadeIn { from{opacity:0;transform:translateX(-8px)} to{opacity:1;transform:translateX(0)} }
  @keyframes sn-pulse  { 0%,100%{opacity:1} 50%{opacity:0.55} }
  .sn-item { animation: sn-fadeIn 0.28s ease both; }
  .sn-child { animation: sn-fadeIn 0.22s ease both; }
`;

// The ONE sidebar for every screen size.
//   variant="desktop" (default) — the fixed, collapsible, resizable sidebar
//                                  shown from the lg breakpoint up.
//   variant="drawer"             — the same sidebar rendered inside the mobile
//                                  slide-out menu (mobile-nav.jsx): always
//                                  expanded, fills the drawer, a close button
//                                  instead of collapse, and onClose() runs
//                                  after any navigation so the drawer shuts.
export function SideNav({ variant = "desktop", onClose } = {}) {
  const isDrawer = variant === "drawer";
  const location = useLocation();
  const pathname = location.pathname;
  const { modules, user, subscription, side: accountSide } = useSelector((state) => state.auth);
  const cssRef = useRef(false);

  const desktopCollapsed = useSideNavCollapsed();
  const collapsed = isDrawer ? false : desktopCollapsed;
  const widthPx = useSideNavWidth();

  // Inject CSS once
  useEffect(() => {
    if (cssRef.current) return;
    cssRef.current = true;
    const el = document.createElement("style");
    el.innerHTML = SIDENAV_CSS;
    document.head.appendChild(el);
  }, []);

  // Drive the global --SideNav-width CSS var (declared in layout.jsx /
  // AdminLayout.jsx's GlobalStyles on body) so the main content's left
  // padding always follows the current collapsed/resized width without
  // those two files needing any state of their own.
  useEffect(() => {
    if (isDrawer) return undefined; // the drawer overlays content — no layout offset
    document.body.style.setProperty("--SideNav-width", `${widthPx}px`);
    return () => { document.body.style.removeProperty("--SideNav-width"); };
  }, [widthPx, isDrawer]);

  // expandedMap: moduleId → boolean
  const [expandedMap, setExpandedMap] = useState({});

  // Final sorted render list — each entry: { module, children } (children
  // recursively shaped the same way), built once per level.
  const [renderList, setRenderList] = useState([]);

  const isAdminArea = pathname.startsWith("/admin");
  const navigate = useNavigate();
  // Profile card at the bottom → the signed-in user's Account page.
  const goToAccount = () => { const p = accountRoute(isAdminArea); if (p) navigate(p); onClose?.(); };

  // Drawer: close it once a link inside is followed (real page links are
  // <a> elements; group-only rows just toggle their dropdown and stay open).
  const closeOnLinkClick = (e) => {
    if (isDrawer && e.target.closest?.("a[href]")) onClose?.();
  };

  useEffect(() => {
    if (!modules || modules.length === 0) return;

    const norm = modules.map(m => ({ ...m, _id: m.moduleId || m._id }));
    // a folder (a main module with no page) owns no permissions — the server lists it when something under it is listed
    const accessible = norm.filter(m => Boolean(m.route_path) && (m.is_group || (m.permissions && m.permissions.length > 0)));

    const getChildren = (parentId) =>
      accessible
        .filter(m => String(m.parent_id) === String(parentId) && String(m.name || "").trim() !== "Store Settings")
        .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));

    // Recursively attach children at every depth — a sub-module that
    // itself has sub-modules gets its own children array (and therefore
    // its own dropdown arrow), not just top-level modules.
    const buildNode = (m) => {
      const children = getChildren(m._id).map(buildNode).filter(Boolean);
      // A main module (folder) with nothing under it that this user may open is not shown at all.
      if (m.is_group && children.length === 0) return null;
      // "Hide in sidebar" ticked on the module: it (and what is under it) is not listed — its pages still open by link
      if (m.hide_in_sidebar) return null;
      return { module: m, children };
    };

    // Master Settings and its children now live in Settings → Master
    // Settings (SettingsPage.jsx) instead of the sidebar — excluded here at
    // the top level so buildNode() never attaches its children either.
    const topLevel = accessible.filter(m => (!m.parent_id || m.parent_id === "") && !CATEGORY_ONLY_NAMES.includes(m.name) && !isSidebarHidden(m, isAdminArea));

    const list = topLevel
      .filter(m => isAdminArea
        ? m.route_path.startsWith("/admin") || m.route_path === routeOf("subscription")
        : !m.route_path.startsWith("/admin"))
      .map(buildNode)
      .filter(Boolean)
      .sort((a, b) => (a.module.order ?? 999) - (b.module.order ?? 999));

    setRenderList(list);

    // Auto-expand items whose descendants match the current path.
    const autoExpand = {};
    const walk = (node) => {
      const childMatch = node.children.some(c => {
        walk(c);
        return autoExpand[c.module._id] || pathname === c.module.route_path || (c.module.route_path && pathname.startsWith(c.module.route_path));
      });
      if (childMatch) autoExpand[node.module._id] = true;
    };
    list.forEach(walk);
    setExpandedMap(prev => ({ ...prev, ...autoExpand }));
  }, [modules, pathname, isAdminArea]);

  const toggle = (id) => setExpandedMap(prev => ({ ...prev, [id]: !prev[id] }));

  // ── Resizable drag handle — drag LEFT to make the sidebar narrower (more room for content); it can not be made
  // wider than its normal width (SIDENAV_EXPANDED_MAX in useSideNavLayout.js).
  const dragRef = useRef(null);
  const startDrag = useCallback((e) => {
    if (collapsed) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startWidth: sideNavLayout.getExpandedWidth() };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const delta = ev.clientX - dragRef.current.startX;
      sideNavLayout.setWidth(dragRef.current.startWidth + delta);
    };
    const onUp = () => {
      dragRef.current = null;
      document.body.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    document.body.style.cursor = "col-resize";
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [collapsed]);

  const nothingToShow = renderList.length === 0;
  if (nothingToShow) return null;

  return (
    <Box sx={isDrawer ? {
      bgcolor: C.bg,
      color: C.text,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      position: "relative",
    } : {
      bgcolor: C.bg,
      color: C.text,
      display: { xs: "none", lg: "flex" },
      flexDirection: "column",
      // Spans the full page height from the very top — MainNav sits beside
      // it in the content column, not above it.
      top: 0,
      height: "100vh",
      position: "fixed",
      width: `${widthPx}px`,
      overflow: "visible",
      borderRight: `1px solid ${C.border}`,
      boxShadow: "4px 0 24px rgba(31,36,48,0.06)",
      transition: dragRef.current ? "none" : "width 0.2s ease",
    }}>

      {/* Top area — brand logo/"MarketSync" text, plus the collapse/expand
          toggle. Collapsed: logo icon stacks above the toggle instead of
          disappearing, so the brand mark stays visible either way. */}
      <Box sx={{
        p: "14px 16px", display: "flex", alignItems: "center",
        flexDirection: collapsed ? "column" : "row",
        justifyContent: collapsed ? "center" : "space-between", gap: collapsed ? "10px" : 1,
      }}>
        {collapsed ? (
          <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: 26, height: "auto", objectFit: "contain", flexShrink: 0 }} />
        ) : (
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
            <Box component="img" src="/assests/website-logo.png" alt="MarketSync" sx={{ width: 26, height: "auto", objectFit: "contain", flexShrink: 0 }} />
            <Typography noWrap sx={{ fontSize: "1rem", fontWeight: 800, letterSpacing: "0.01em" }}>
              <Box component="span" sx={{ color: C.text }}>Market</Box>
              <Box component="span" sx={{ background: `linear-gradient(135deg,${C.orangeFrom},${C.orangeTo})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>Sync</Box>
            </Typography>
          </Box>
        )}
        <Box
          onClick={() => (isDrawer ? onClose?.() : sideNavLayout.setCollapsed(!collapsed))}
          title={isDrawer ? "Close menu" : collapsed ? "Expand sidebar" : "Collapse sidebar"}
          sx={{
            width: 26, height: 26, borderRadius: "50%", flexShrink: 0,
            background: C.surface, border: `1px solid ${C.border}`,
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
            color: C.text, transition: "background 0.2s",
            "&:hover": { background: C.borderSoft },
          }}
        >
          {isDrawer ? <CloseRoundedIcon sx={{ fontSize: 16 }} /> : collapsed ? <ChevronRightRoundedIcon sx={{ fontSize: 16 }} /> : <ChevronLeftRoundedIcon sx={{ fontSize: 16 }} />}
        </Box>
      </Box>

      <Box sx={{ mx: "12px", height: "1px", background: `linear-gradient(90deg, transparent, ${C.border}, transparent)` }} />

      {/* Company & Store filter — admin side, only while the sidebar is open
          (hidden on the collapsed icon rail). */}
      {/* admin side always; customer side on every paid plan (not on the Free plan, which shows demo data only) */}
      {!collapsed && (isAdminArea || (accountSide === "customer" && subscription && !subscription.isFree)) && <SideNavScopeFilter />}

      {/* Nav list */}
      <Box onClick={closeOnLinkClick} sx={{
        flex: "1 1 auto",
        // Without this, a flex child refuses to shrink below its own
        // content's natural height — on a short viewport the nav list
        // would just push the user card/logout/footer below it instead
        // of scrolling internally and keeping them pinned in view.
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        px: collapsed ? "8px" : "10px",
        py: "14px",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { background: C.border, borderRadius: "4px" },
      }}>
        <Stack component="ul" spacing={0.5} sx={{ listStyle: "none", m: 0, p: 0 }}>
          {renderList.map((node, idx) => (
            <Box component="li" key={node.module._id} className="sn-item" sx={{ animationDelay: `${idx * 0.04}s` }}>
              <NavNode node={node} depth={0} pathname={pathname} expandedMap={expandedMap} toggle={toggle} collapsed={collapsed} />
            </Box>
          ))}
        </Stack>
      </Box>

      <Box sx={{ mx: "12px", height: "1px", background: `linear-gradient(90deg, transparent, ${C.border}, transparent)` }} />

      {/* Status card — real signed-in user (name, role, real profile
          picture when set). Collapsed: same info, stacked, icon-only. */}
      {user && (
        collapsed ? (
          <Stack alignItems="center" spacing={1} onClick={goToAccount} title="My Account" sx={{ my: "10px", cursor: "pointer" }}>
            <UserAvatar user={user} size={32} />
            <Typography noWrap sx={{ fontSize: "0.58rem", fontWeight: 600, color: C.textMuted, maxWidth: 60, textAlign: "center" }}>
              {(user?.name || user?.email || "Account").split(" ")[0]}
            </Typography>
          </Stack>
        ) : (
          <Box onClick={goToAccount} title="My Account" sx={{ mx: "12px", my: "10px", p: "10px 12px", borderRadius: "12px", background: C.surface, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", gap: 1, cursor: "pointer", transition: "border-color 0.2s ease", "&:hover": { borderColor: "#d8b25a" } }}>
            <UserAvatar user={user} size={34} />
            <Box sx={{ minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.text }}>{user?.name || user?.email || "Account"}</Typography>
              {accountSide === "customer" ? (
                // customer side: the CURRENT PLAN in a chip, where the role used to be
                <Box component="span" title="Your current plan" sx={{
                  display: "inline-block", mt: 0.2, px: 0.9, py: 0.1, borderRadius: "999px", fontSize: "0.6rem", fontWeight: 800, letterSpacing: "0.04em",
                  textTransform: "uppercase", background: subscription?.name ? "linear-gradient(135deg, #d8b25a, #b8892c)" : "#e9e1cd", color: subscription?.name ? "#fff" : "#7a7060", whiteSpace: "nowrap",
                }}>{subscription?.name || "No plan"}</Box>
              ) : (
                <Typography noWrap sx={{ fontSize: "0.66rem", color: C.textMuted, textTransform: "capitalize" }}>{(user?.role || "").replace(/-/g, " ") || "Signed in"}</Typography>
              )}
            </Box>
          </Box>
        )
      )}

      {/* Logout — the true last element in the sidebar, below everything
          else, so it's never at risk of being clipped inside another box. */}
      {user && (collapsed ? (
        <Stack alignItems="center" sx={{ pb: "12px" }}><LogoutButton collapsed /></Stack>
      ) : (
        <Box sx={{ mx: "12px", mb: "12px", borderRadius: "10px", border: `1px solid ${C.border}`, overflow: "hidden" }}>
          <LogoutButton />
        </Box>
      ))}

      {/* Resize drag handle — right edge, expanded desktop sidebar only (narrower only, see above) */}
      {!collapsed && !isDrawer && (
        <Box
          onMouseDown={startDrag}
          sx={{
            position: "absolute", top: 0, right: 0, width: 6, height: "100%",
            cursor: "w-resize", zIndex: 1,
            "&:hover": { background: `linear-gradient(90deg, transparent, ${C.orangeTo}33)` },
          }}
        />
      )}
    </Box>
  );
}

// ── NavNode — recursive: renders a module row at any depth. Only shows
// a dropdown chevron when this module actually has children; a leaf
// module (at ANY depth, not just top-level) is always a plain link. ──
function NavNode({ node, depth, pathname, expandedMap, toggle, collapsed }) {
  const { module: m, children } = node;
  const hasChildren = children.length > 0;
  const isChild = depth > 0;

  if (collapsed && depth === 0) {
    return <CollapsedNavItem node={node} pathname={pathname} expandedMap={expandedMap} toggle={toggle} />;
  }

  if (!hasChildren) {
    return <NavItem href={m.route_path} icon={m.icon} pathname={pathname} title={m.name} isChild={isChild} />;
  }

  const isExpanded = !!expandedMap[m._id];
  const isAnyDescendantActive = (node) => node.children.some(c =>
    pathname === c.module.route_path || (c.module.route_path && pathname.startsWith(c.module.route_path)) || isAnyDescendantActive(c)
  );
  const isAnyChildActive = isAnyDescendantActive(node);
  const isParentActive = isNavItemActive({ href: m.route_path, pathname });

  return (
    <Box>
      {/* Parent row — left side navigates, right side (arrow) toggles dropdown */}
      <Box sx={{
        display: "flex",
        alignItems: "center",
        borderRadius: "10px",
        overflow: "hidden",
        transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
        color: C.text,
        borderLeft: isParentActive ? `3px solid ${C.orangeTo}` : "3px solid transparent",
        background: isParentActive
          ? `${C.orangeTo}2e`
          : isAnyChildActive ? `${C.orangeTo}16` : "transparent",
        boxShadow: isParentActive ? "0 2px 10px rgba(228,135,14,0.14)" : "none",
        "&:hover": {
          background: isParentActive ? `${C.orangeTo}3a` : C.borderSoft,
        },
      }}>
        <Box
          {...(isRealPage(m) ? { component: Link, to: m.route_path } : {})}
          onClick={() => toggle(m._id)}
          sx={{ flex: 1, display: "flex", alignItems: "center", gap: "10px", pl: isChild ? "9px" : "11px", pr: isChild ? "10px" : "12px", py: isChild ? "7px" : "9px", textDecoration: "none", color: "inherit", cursor: "pointer" }}
        >
          <ModuleIcon icon={m.icon} active={isParentActive || isAnyChildActive} size={isChild ? 15 : 18} />
          <Typography sx={{ fontSize: isChild ? "0.8125rem" : "0.875rem", fontWeight: (isParentActive || isAnyChildActive) ? 700 : isChild ? 400 : 500, letterSpacing: 0.2, color: "inherit" }}>
            {m.name}
          </Typography>
        </Box>

        {/* Single dropdown indicator — colored gold when this row is
            active, instead of a second separate arrow duplicating it. */}
        <Box
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(m._id); }}
          sx={{
            display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: "100%", minHeight: 36,
            flexShrink: 0, cursor: "pointer", borderLeft: "1px solid rgba(20,22,28,0.08)",
            "&:hover": { background: "rgba(20,22,28,0.06)" },
          }}
        >
          <Box sx={{
            display: "flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: "50%",
            background: isParentActive ? `${C.orangeTo}33` : isExpanded ? "rgba(20,22,28,0.12)" : "rgba(20,22,28,0.05)",
            color: isParentActive ? C.orangeTo : "inherit",
            transition: "all 0.25s",
          }}>
            {isExpanded ? <ExpandLess sx={{ fontSize: 13, color: "inherit" }} /> : <ExpandMore sx={{ fontSize: 13, color: "inherit" }} />}
          </Box>
        </Box>
      </Box>

      {/* Children — recurse, so a sub-module with its own sub-modules
          gets its own dropdown arrow too. */}
      <Collapse in={isExpanded} timeout={220}>
        <Box sx={{ pl: "8px", mt: "4px", mb: "4px" }}>
          <Box sx={{ ml: "20px", pl: "14px", borderLeft: `2px solid ${C.orangeTo}40` }}>
            <Stack spacing={0.4}>
              {children.map((child, ci) => (
                <Box key={child.module._id} className="sn-child" sx={{ animationDelay: `${ci * 0.04}s` }}>
                  <NavNode node={child} depth={depth + 1} pathname={pathname} expandedMap={expandedMap} toggle={toggle} collapsed={false} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}

// Small shared icon renderer with a fallback dot so every row — parent or
// child, at any depth — always shows SOME icon, even if a module's data
// doesn't have one set.
function ModuleIcon({ icon, active, size = 18 }) {
  const known = icon && navIcons[icon];
  const Icon = known || CircleIcon;
  return (
    <Icon
      fill={active ? "#14161c" : C.textSub}
      fontSize={known ? `${size}px` : `${size * 0.5}px`}
      weight={known ? (active ? "fill" : undefined) : "fill"}
      style={{ flexShrink: 0, transition: "fill 0.22s" }}
    />
  );
}

// ── Collapsed top-level item — icon centered, tiny label underneath. ──
function CollapsedNavItem({ node, pathname, expandedMap, toggle }) {
  const { module: m, children } = node;
  const hasChildren = children.length > 0;
  const isDescendantActive = (n) => n.children.some(c =>
    pathname === c.module.route_path || (c.module.route_path && pathname.startsWith(c.module.route_path)) || isDescendantActive(c)
  );
  const active = isNavItemActive({ href: m.route_path, pathname }) || (hasChildren && isDescendantActive(node));
  const [hovered, setHovered] = useState(false);
  const isOpen = hasChildren && !!expandedMap[m._id];

  const body = (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "4px",
      py: "10px", px: "4px", borderRadius: "10px",
      background: active ? `${C.orangeTo}2e` : (hovered || isOpen) ? C.borderSoft : "transparent",
      border: active ? `1px solid ${C.orangeTo}55` : "1px solid transparent",
      boxShadow: active ? "0 2px 10px rgba(228,135,14,0.14)" : "none",
      transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
    }}>
      <ModuleIcon icon={m.icon} active={active} size={19} />
      <Typography noWrap sx={{ fontSize: "0.6rem", fontWeight: 600, color: active ? "#14161c" : C.textMuted, maxWidth: 60, textAlign: "center", lineHeight: 1.15 }}>
        {m.name}
      </Typography>
      {/* A module WITH sub-modules gets its own arrow, sized to actually
          be visible, sitting below the name — clicking it expands the
          sub-module icons right here, inline, without leaving the rail. */}
      {hasChildren && (
        <Box sx={{
          width: 22, height: 22, borderRadius: "50%", mt: "2px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: active ? "rgba(20,22,28,0.2)" : "rgba(20,22,28,0.08)", color: active ? "#14161c" : C.textSub,
        }}>
          {isOpen ? <ExpandLess sx={{ fontSize: 15, color: "inherit" }} /> : <ExpandMore sx={{ fontSize: 15, color: "inherit" }} />}
        </Box>
      )}
    </Box>
  );

  if (!hasChildren) {
    return (
      <Box component={Link} to={m.route_path} title={m.name} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} sx={{ textDecoration: "none", display: "block" }}>
        {body}
      </Box>
    );
  }

  return (
    <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <Box
        {...(isRealPage(m) ? { component: Link, to: m.route_path } : {})}
        onClick={() => toggle(m._id)}
        title={m.name}
        sx={{ cursor: "pointer", textDecoration: "none", display: "block" }}
      >
        {body}
      </Box>
      {/* Sub-modules stack inline, right below the icon, in the same
          narrow column — no flyout/popup, matching the reference layout. */}
      <Collapse in={isOpen} timeout={200}>
        <Stack spacing={0.4} sx={{ alignItems: "center", pt: "4px", pb: "2px" }}>
          {children.map((child) => (
            <CollapsedChildIcon key={child.module._id} node={child} pathname={pathname} expandedMap={expandedMap} toggle={toggle} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ── CollapsedChildIcon — a sub-module row inside the collapsed rail:
// icon only (no label — there's no room once nested), with its own
// chevron + inline children if IT also has sub-modules. ──────────────
function CollapsedChildIcon({ node, pathname, expandedMap, toggle }) {
  const { module: m, children } = node;
  const hasChildren = children.length > 0;
  const active = isNavItemActive({ href: m.route_path, pathname });
  const isOpen = hasChildren && !!expandedMap[m._id];

  const body = (
    <Box sx={{
      display: "flex", flexDirection: "column", alignItems: "center", gap: "3px",
      py: "6px", px: "4px", borderRadius: "9px",
      background: active ? `${C.orangeTo}2e` : "transparent",
      border: active ? `1px solid ${C.orangeTo}55` : "1px solid transparent",
      transition: "background 0.25s ease, border-color 0.25s ease",
      "&:hover": { background: active ? undefined : C.borderSoft },
    }}>
      <ModuleIcon icon={m.icon} active={active} size={15} />
      <Typography noWrap sx={{ fontSize: "0.56rem", fontWeight: 600, color: active ? "#14161c" : C.textMuted, maxWidth: 60, textAlign: "center", lineHeight: 1.1 }}>
        {m.name}
      </Typography>
      {hasChildren && (
        <Box sx={{
          width: 17, height: 17, borderRadius: "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: active ? "rgba(20,22,28,0.2)" : "rgba(20,22,28,0.08)", color: active ? "#14161c" : C.textSub,
        }}>
          {isOpen ? <ExpandLess sx={{ fontSize: 11, color: "inherit" }} /> : <ExpandMore sx={{ fontSize: 11, color: "inherit" }} />}
        </Box>
      )}
    </Box>
  );

  if (!hasChildren) {
    return (
      <Box component={Link} to={m.route_path} title={m.name} sx={{ textDecoration: "none", display: "block" }}>
        {body}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
      <Box
        {...(isRealPage(m) ? { component: Link, to: m.route_path } : {})}
        onClick={() => toggle(m._id)}
        title={m.name}
        sx={{ cursor: "pointer", textDecoration: "none", display: "block" }}
      >
        {body}
      </Box>
      <Collapse in={isOpen} timeout={200}>
        <Stack spacing={0.4} sx={{ alignItems: "center", pt: "4px" }}>
          {children.map((c) => (
            <CollapsedChildIcon key={c.module._id} node={c} pathname={pathname} expandedMap={expandedMap} toggle={toggle} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

// ── NavItem — a plain (childless) module row, any depth. ──────────────
function NavItem({ href, icon, pathname, title, isChild = false }) {
  const active = isNavItemActive({ href, pathname });
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      component={Link}
      to={href}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        pl: isChild ? "9px" : "11px",
        pr: isChild ? "10px" : "12px",
        py: isChild ? "7px" : "9px",
        borderRadius: "10px",
        textDecoration: "none",
        transition: "background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease",
        cursor: "pointer",
        position: "relative",
        borderLeft: active ? `3px solid ${C.orangeTo}` : "3px solid transparent",
        background: active ? `${C.orangeTo}2e` : hovered ? C.borderSoft : "transparent",
        boxShadow: active ? "0 2px 10px rgba(228,135,14,0.14)" : "none",
        color: active ? C.text : C.text,
      }}
    >
      <ModuleIcon icon={icon} active={active} size={isChild ? 15 : 18} />

      {/* No trailing arrow here — that's reserved for modules that
          actually have sub-modules (NavNode's parent row below). A plain
          leaf item has nothing further to point to. */}
      <Typography sx={{ fontSize: isChild ? "0.8125rem" : "0.875rem", fontWeight: active ? 700 : isChild ? 400 : 500, letterSpacing: 0.2, color: "inherit", flex: 1 }}>
        {title}
      </Typography>
    </Box>
  );
}

// ── UserAvatar — the user's real profile picture (same field main-nav.jsx
// reads, user.profilePicture) when set, else an initial-letter badge. ──
function UserAvatar({ user, size = 32 }) {
  const initial = (user?.name || user?.email || "U").charAt(0).toUpperCase();
  return (
    <Avatar
      src={user?.profilePicture}
      sx={{
        width: size, height: size, flexShrink: 0,
        fontSize: `${size * 0.42}px`, fontWeight: 800,
        background: "linear-gradient(135deg, #fbf3df, #f0dcae)",
        color: "#7a5712",
        border: "1.5px solid rgba(216,178,90,0.6)",
      }}
    >
      {!user?.profilePicture && initial}
    </Avatar>
  );
}

// ── LogoutButton — same logout flow as main-nav.jsx's handleLogout
// (remembers the current page, redirects to the right login screen). ──
function LogoutButton({ collapsed = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleLogout = () => {
    const currentPath = window.location.pathname + window.location.search;
    const isAdminPath = currentPath.startsWith("/admin");
    sessionStorage.setItem(isAdminPath ? "lastAdminPage" : "lastUserPage", currentPath);
    dispatch(logout());
    navigate(loginRoute(isAdminPath), { replace: true });
  };

  if (collapsed) {
    return (
      <Box onClick={handleLogout} title="Logout" sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px", cursor: "pointer", color: "#dc2626", "&:hover": { opacity: 0.8 } }}>
        <SignOut size={17} weight="bold" />
        <Typography sx={{ fontSize: "0.58rem", fontWeight: 600 }}>Logout</Typography>
      </Box>
    );
  }

  return (
    <Box
      onClick={handleLogout}
      sx={{
        display: "flex", alignItems: "center", gap: 1, px: "12px", py: "9px", cursor: "pointer",
        background: C.surface, color: "#dc2626", transition: "background 0.2s",
        "&:hover": { background: "rgba(220,38,38,0.06)" },
      }}
    >
      <SignOut size={15} weight="bold" />
      <Typography sx={{ fontSize: "0.76rem", fontWeight: 700 }}>Logout</Typography>
    </Box>
  );
}
